/**
 * What a governed semantic view offers the query builder: its metrics,
 * dimensions and date fields, spelled `table.member` exactly as the view
 * exposes them, the small value sets a form can offer, and which tables one
 * query may read together. The server owns it (`analytics/snowflake_query`
 * `catalog.go`, the same description its validator applies) and serves it from
 * `GET …/snowflake_query/catalog`; `viewCatalogOptions` fetches it. Nothing here
 * knows the views: the helpers that read a spec without a catalog fall back to
 * what member names say (`*_date`, `*_rate`).
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from '@customerio/hydra-validation';
import { getApiClientErrorStatus, hydraUiApi } from '@/services/api';
import { analyticsQueryKeys } from './query-keys';

export type FieldType = 'string' | 'number' | 'bool' | 'date' | 'enum';

export interface ViewCatalog {
  view: string;
  label: string;
  /**
   * Dotted date dimensions. A new time window uses the first one on the fact
   * table the metrics come from, or on a table related to it; see
   * `defaultDateField`.
   */
  dateFields: string[];
  /** Dotted metric names. Every metric is numeric and filters by its bare name. */
  metrics: string[];
  /** Dotted dimension names, date fields included. */
  dimensions: string[];
  /** Known small value sets, by bare member name. */
  enums: Record<string, string[]>;
  /**
   * Each fact table (the tables metrics come from) with the dimension tables
   * it joins to. One query reads one fact table and only its related tables.
   */
  relationships: Record<string, string[]>;
}

// Go encodes an empty slice or map it never allocated as null; read it as empty.
const names = z
  .array(z.string())
  .nullish()
  .transform((list) => list ?? []);
const namesByKey = z
  .record(z.string(), names)
  .nullish()
  .transform((map) => map ?? {});
const viewSchema = z.object({
  view: z.string(),
  label: z.string(),
  date_fields: names,
  metrics: names,
  dimensions: names,
  enums: namesByKey,
  relationships: namesByKey,
});
const catalogResponseSchema = z.object({ views: z.array(viewSchema) });

function viewCatalogFromApi(view: z.infer<typeof viewSchema>): ViewCatalog {
  return {
    view: view.view,
    label: view.label,
    dateFields: view.date_fields,
    metrics: view.metrics,
    dimensions: view.dimensions,
    enums: view.enums,
    relationships: view.relationships,
  };
}

/**
 * The catalog of every governed view, by view name. Static per deploy, so it
 * is fetched once per workspace and kept. A workspace the flag gate refuses
 * (an answer opened after the flag went off) gets an empty catalog, which the
 * form treats as nothing to offer; that is not an error worth reporting.
 */
export function viewCatalogOptions(workspaceId: number) {
  return queryOptions({
    queryKey: analyticsQueryKeys.catalog(workspaceId),
    queryFn: async (): Promise<Map<string, ViewCatalog>> => {
      try {
        const response = await hydraUiApi.snowflake.getSnowflakeQueryCatalog({
          params: { path: { environment_id: String(workspaceId) } },
        });
        const { views } = catalogResponseSchema.parse(response);
        return new Map(views.map((view) => [view.view, viewCatalogFromApi(view)]));
      } catch (error) {
        if (getApiClientErrorStatus(error) === 403) return new Map();
        throw error;
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    meta: { captureExceptions: true },
  });
}

/** The bare output name of a `table.member`. */
export function bareName(member: string): string {
  const dot = member.lastIndexOf('.');
  return dot === -1 ? member : member.slice(dot + 1);
}

/** The table of a `table.member`, lowercased as the server reads it, or null for a bare name. */
function tableOf(member: string): string | null {
  const dot = member.indexOf('.');
  return dot === -1 ? null : member.slice(0, dot).toLowerCase();
}

/** The fact table a query reads: the table of its first metric. */
export function factTableOf(metrics: string[]): string | null {
  const first = metrics[0];
  return first == null ? null : tableOf(first);
}

/**
 * Whether a member may be read by a query on `fact`: the same rule the server's
 * validator applies. A table the relationships name must be the fact table or
 * one related to it; a table they do not name, or an unknown fact table, is
 * left alone, so nothing is held back that the server would accept.
 */
export function isReadableWith(catalog: ViewCatalog, fact: string | null, member: string): boolean {
  const table = tableOf(member);
  if (fact == null || table == null) return true;
  const related = catalog.relationships[fact];
  if (related == null) return true;
  const known = new Set(Object.entries(catalog.relationships).flatMap(([f, tables]) => [f, ...tables]));
  return !known.has(table) || table === fact || related.includes(table);
}

/**
 * The date dimension a time window on `fact` uses: the fact table's own first,
 * then one on a table the query can read.
 */
export function defaultDateField(catalog: ViewCatalog, fact: string | null): string | null {
  return (
    catalog.dateFields.find((field) => tableOf(field) === fact) ??
    catalog.dateFields.find((field) => isReadableWith(catalog, fact, field)) ??
    null
  );
}

/**
 * The type of a member, for picking operators and the value control. Metrics
 * are numbers; dimensions are typed by what the playbooks say about them,
 * which their names carry: `*_date`, `is_*`/`was_*`/`*_tracked`, ids and hours.
 */
export function fieldType(catalog: ViewCatalog | null, field: string, specMetrics: string[] = []): FieldType {
  const name = bareName(field);
  // The spec's own metrics are numbers whether or not the catalog has arrived.
  if (specMetrics.some((m) => m === field || bareName(m) === name)) return 'number';
  if (catalog != null && catalog.metrics.some((m) => m === field || bareName(m) === name)) return 'number';
  if (catalog?.enums[name] != null) return 'enum';
  if (name.endsWith('_date')) return 'date';
  if (/^(is|was)_|_(tracked|holdout|enabled|archived|deleted)$/.test(name)) return 'bool';
  if (/(^|_)id$|_hour$|hours_since_message$|_ids$/.test(name)) return 'number';
  return 'string';
}

/**
 * Whether a metric is a rate, a 0-1 fraction rather than a count: every
 * `*_rate` member, and the click-to-open rates (`ctor`, `human_ctor`,
 * `unique_ctor`). Named in the catalog's own spelling, so a member added under
 * either pattern is read as a rate without an entry here.
 */
export function isRateMetric(member: string): boolean {
  return /(^|_)(rate|ctor)$/.test(bareName(member).toLowerCase());
}
